/* Cart page enhancements.
 *
 * Three jobs, all of which the page can survive losing.
 *
 * 1. The quantity input. Plus and minus are ordinary links to /cart/change, so
 *    they work with scripting off; this makes a typed value behave the same way
 *    by navigating to the same URL. Without it the field is still readable and
 *    the buttons still work, which is why it is an enhancement and not the
 *    mechanism.
 *
 * 2. The discount field. Shopify applies a code at /discount/CODE, which is a
 *    navigation and not a post, so there is no form to submit and this does the
 *    navigating. Nothing here can tell the shopper whether the code is good: the
 *    line under the field says the discount appears at checkout, because that is
 *    the only place it can be known.
 *
 * 3. The delivery date on each line, from PIM's In Your Hands. The line ships
 *    hidden and is revealed only when PIM answers, so a cart that cannot reach
 *    the service shows the stock state alone rather than an empty truck.
 */
(function () {
  var root = document.querySelector('.aps-cart');
  if (!root) return;

  /* The change route, read off a link rather than assumed. Locale prefixed
     stores serve /fr-ca/cart/change, and hardcoding /cart/change sends those
     shoppers to a 404 that only shows up in the one market nobody tests. */
  function changeUrl() {
    var a = root.querySelector('.crt-qty a[href*="/cart/change"]');
    if (!a) return null;
    return a.getAttribute('href').split('?')[0];
  }

  root.addEventListener('change', function (e) {
    var input = e.target;
    if (!input.matches || !input.matches('.crt-qty input[data-line]')) return;
    var url = changeUrl();
    if (!url) return;
    var qty = parseInt(input.value, 10);
    if (isNaN(qty) || qty < 0) return;
    window.location.href = url + '?line=' + encodeURIComponent(input.dataset.line) +
      '&quantity=' + encodeURIComponent(qty);
  });

  var field = root.querySelector('#aps-discount');
  var go = root.querySelector('#aps-discount-go');
  if (field && go) {
    var apply = function () {
      var code = (field.value || '').trim();
      if (!code) { field.focus(); return; }
      /* Shopify applies the code and then honours redirect, so the shopper lands
         back on the cart with it applied rather than being thrown into checkout
         by a field they were only trying out. */
      window.location.href = '/discount/' + encodeURIComponent(code) + '?redirect=/cart';
    };
    go.addEventListener('click', apply);
    field.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter') return;
      /* The field lives inside the cart form, so Enter would otherwise submit it
         and send the shopper to checkout without the code. */
      e.preventDefault();
      apply();
    });
  }

  /* ---- the delivery date on each line ------------------------------------
   *
   * One request per line, on purpose. The service takes a cart map and returns
   * a single estimate for it, and that estimate collapses to the slowest line:
   * measured on 2026-09-06, a parcel item alone answered Tue Sep 8, a freight
   * item on an eleven day backorder answered Thu Sep 18, and the two together
   * answered Sep 18 for the pair. Asked one SKU at a time each line gets its
   * own date. A cart has a handful of lines, so a handful of requests is the
   * right trade; a collection grid of 24 cards is not, which is why the same
   * treatment on a card is still an open question.
   *
   * The zip is PIM's own localStorage key, written by assets/aps-delivery.js
   * when the header chip changes it. With no zip PIM resolves the destination
   * from the caller's IP, which is the same thing the header does on a first
   * visit, so an absent key is not a reason to skip the request.
   */
  var ENDPOINT = 'https://pim.poolzoom.com/api/routing/cart-estimate';
  var CHANNEL = 16;

  function zip() {
    try { return localStorage.getItem('inYourHandsZip') || ''; } catch (e) { return ''; }
  }

  /* PIM returns UTC. A date rendered from it has to be read in the shopper's own
     zone or a late afternoon cutoff shows as the following day. */
  function label(iso) {
    var d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' });
  }

  function ask(sku, signal) {
    var body = { cart: {}, current: {}, channel: CHANNEL };
    body.cart[sku] = 1;
    body.current[sku] = 1;
    var z = zip();
    if (z) body.zip = z;
    return fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal: signal
    }).then(function (r) { return r.ok ? r.json() : null; });
  }

  var wakeTimer = null;
  var nextWake = null;

  function fillDates() {
    var slots = root.querySelectorAll('[data-aps-when]');
    /* A cart of nothing but special orders has no date to ask for, since Liquid printed each
       line's lead time, but its promise still has to reach the cart attribute. */
    if (!slots.length) {
      if (window.apsDelivery && window.apsDelivery.syncPromise) window.apsDelivery.syncPromise();
      return;
    }

    /* One controller for the page. A cart that is being edited navigates away
       mid-flight, and an aborted fetch is quieter than a rejected one. */
    var controller = new AbortController();
    var timer = setTimeout(function () { controller.abort(); }, 6000);
    var outstanding = slots.length;
    function settled() {
      outstanding -= 1;
      if (outstanding === 0) {
        clearTimeout(timer);
        /* Every line has answered, or failed. Write what was shown to the cart
           attribute now, after the dates have painted, never before. */
        if (window.apsDelivery && window.apsDelivery.syncPromise) window.apsDelivery.syncPromise();
      }
    }

    Array.prototype.forEach.call(slots, function (slot) {
      var sku = slot.getAttribute('data-aps-when');
      ask(sku, controller.signal).then(function (data) {
        if (window.apsDelivery && window.apsDelivery.recordPromise) window.apsDelivery.recordPromise(sku, data);
        /* Re-ask every line the moment the earliest cutoff passes, so a cart left
           open rolls its dates and badges forward with PIM rather than sitting on
           a promise the clock has taken away. */
        var at = window.apsDelivery && window.apsDelivery.cutoffAt ? window.apsDelivery.cutoffAt(data) : null;
        if (at && (!nextWake || at < nextWake)) {
          nextWake = at;
          clearTimeout(wakeTimer);
          wakeTimer = setTimeout(function () { nextWake = null; fillDates(); }, at - Date.now() + 1500);
        }
        settled();
        /* Two different answers arrive with an empty warehouse_id and they must
           not be treated alike.

           A SKU the service does not know comes back with no warehouse, no
           backorder and a date it cannot stand behind. Suppress that: the stock
           state is still true, so the line keeps it and says nothing about when.

           A known line that is backordered also has no warehouse, because it is
           not in one yet, but it carries backorder_handling as a number of days
           and a real date at the end of them. That is the most useful thing the
           service can say about that line, so it is shown.

           Measured 2026-09-06: CAL-187331 answered warehouse "", freight,
           backorder_handling 11, Sep 18. An unknown key answered warehouse "",
           parcel, backorder_handling null. Two samples, so this is the shape
           worth confirming with PIM rather than a documented contract. */
        if (!data || !data.delivery_estimate) return;

        /* The badge shares the row and the answer. The tier logic lives in
           aps-delivery.js so the product page and the cart cannot drift; if that
           script is missing the line keeps its date and shows no badge. */
        var badge = slot.parentNode.querySelector('[data-aps-tier]');
        var store = window.apsDelivery;
        if (badge && store && store.tier) {
          var tier = store.tier(data);
          if (tier) {
            // FREE leads the tier when the cart ships free, decided in Liquid from the
            // cart total, the same way the line's free word is.
            var free = badge.getAttribute('data-free') === 'true';
            badge.querySelector('[data-aps-tier-label]').textContent = (free ? 'FREE ' : '') + store.tierLabel(tier);
            badge.setAttribute('data-tier', tier);
            badge.hidden = false;
          } else {
            badge.hidden = true;
          }
        }

        var routed = data.warehouse_id || typeof data.backorder_handling === 'number';
        if (!routed) return;
        var text = label(data.delivery_estimate);
        if (!text) return;
        slot.querySelector('[data-aps-when-date]').textContent = text;
        slot.hidden = false;
      }).catch(function () { settled(); });
    });
  }

  fillDates();

  /* The header chip can change the zip after this has already run. Repaint from
     the new one; never announce back, which is what keeps the chip and this from
     talking each other in circles. */
  document.addEventListener('aps:delivery', function (event) {
    if (!event.detail || !event.detail.zip) return;
    fillDates();
  });
}());
